// /src/Components/Domain/components/DomainSetupForm.jsx

import React, { useContext, useMemo } from "react";
import {
  Globe2,
  LockKeyhole,
  RefreshCcw,
} from "lucide-react";
import { GlobalContext } from "../../../Context/GlobalContext";
import ClientSearchSelect from "../../common/ClientSearchSelect";
import TemplateSearchSelect from "../TemplateSearchSelect";

export default function DomainSetupForm({
  domain,
  fixedContentIp,

  templates,
  templatesLoading,
  fetchTemplates,

  selectedClientId,
  setSelectedClientId,

  selectedTemplateId,
  setSelectedTemplateId,

  selectedTemplateCode,
  setSelectedTemplateCode,

  subdomainName,
  setSubdomainName,

  creating,
}) {
  const { AllClients = [] } = useContext(GlobalContext);

  // Convert GlobalContext clients into ClientSearchSelect format
  const clients = useMemo(() => {
    return (AllClients || []).map((client) => ({
      ...client,

      us_code:
        client?.us_code ||
        client?.userid ||
        client?.userId ||
        client?.value ||
        "",

      us_name:
        client?.organization_name ||
        client?.us_name ||
        client?.us_loginid ||
        client?.us_email ||
        client?.label ||
        "Client",
    }));
  }, [AllClients]);

  const cleanSubdomain = String(subdomainName || "")
    .trim()
    .toLowerCase();

  const dynamicHost = cleanSubdomain
    ? `${cleanSubdomain}.${domain}`
    : `restaurant.${domain}`;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
      {/* ==================================================
          First row: Client
      ================================================== */}

      <div className="lg:col-span-4">
        <label className="mb-1 block text-xs font-semibold text-gray-500">
          Client
        </label>

        <ClientSearchSelect
          clients={clients}
          value={selectedClientId || ""}
          disabled={creating}
          placeholder={
            clients.length === 0
              ? "No clients available"
              : "Select client"
          }
          searchPlaceholder="Search client by name or code..."
          className="w-full [&>button]:h-11 [&>button]:rounded-xl [&>button]:border-gray-200"
          onChange={(client) => {
            setSelectedClientId?.(client?.us_code || "");
          }}
        />

        <p className="mt-1 text-xs text-gray-400">
          User ID:{" "}
          <span className="font-mono text-gray-600">
            {selectedClientId || "—"}
          </span>
        </p>
      </div>

      {/* ==================================================
          First row: Template
      ================================================== */}

      <div className="lg:col-span-4">
        <label className="mb-1 block text-xs font-semibold text-gray-500">
          Template
        </label>

        <TemplateSearchSelect
          templates={templates}
          value={selectedTemplateId || ""}
          loading={templatesLoading}
          disabled={templatesLoading || creating}
          placeholder="Select website template"
          onChange={(template) => {
            setSelectedTemplateId(template?.id || "");

            setSelectedTemplateCode(
              template?.template_code || "",
            );
          }}
        />

        <div className="mt-1 flex items-center justify-between gap-2">
          <p className="min-w-0 truncate text-xs text-gray-400">
            Code:{" "}
            <span className="font-mono text-gray-600">
              {selectedTemplateCode || "—"}
            </span>
            {" · "}
            ID:{" "}
            <span className="font-mono text-gray-600">
              {selectedTemplateId || "—"}
            </span>
          </p>

          <button
            type="button"
            onClick={fetchTemplates}
            disabled={templatesLoading || creating}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCcw
              size={12}
              className={templatesLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* ==================================================
          First row: Restaurant subdomain
      ================================================== */}

      <div className="lg:col-span-4">
        <label className="mb-1 block text-xs font-semibold text-gray-500">
          Restaurant Subdomain
        </label>

        <input
          type="text"
          value={subdomainName || ""}
          disabled={creating}
          placeholder="e.g. restaurant-name"
          onChange={(event) => {
            const value = event.target.value
              .toLowerCase()
              .replace(/\s+/g, "-")
              .replace(/[^a-z0-9-]/g, "");

            setSubdomainName(value);
          }}
          className="h-11 w-full rounded-xl border bg-white px-3 text-sm outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:bg-gray-50"
        />

        <p className="mt-1 truncate text-xs text-gray-400">
          Host:{" "}
          <span className="font-mono text-gray-600">
            {dynamicHost}
          </span>
        </p>
      </div>

      {/* ==================================================
          Second row: Fixed base domain
      ================================================== */}

      <div className="lg:col-span-6">
        <label className="mb-1 block text-xs font-semibold text-gray-500">
          Base Domain
        </label>

        <div className="flex h-11 items-center justify-between gap-3 rounded-xl border bg-gray-50 px-3">
          <div className="flex items-center gap-2">
            <Globe2 size={16} className="text-emerald-600" />

            <span className="font-mono text-sm font-medium text-gray-700">
              {domain}
            </span>
          </div>

          <span className="rounded-full bg-gray-200 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-600">
            Fixed
          </span>
        </div>

        <p className="mt-1 text-xs text-gray-400">
          All new restaurant subdomains use this base domain.
        </p>
      </div>

      {/* ==================================================
          Second row: Fixed content IP
      ================================================== */}

      <div className="lg:col-span-6">
        <label className="mb-1 block text-xs font-semibold text-gray-500">
          Content IP
        </label>

        <div className="flex h-11 items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3">
          <div className="flex items-center gap-2">
            <LockKeyhole size={16} className="text-emerald-600" />

            <span className="font-mono text-sm font-semibold text-emerald-800">
              {fixedContentIp}
            </span>
          </div>

          <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
            Preset
          </span>
        </div>

        <p className="mt-1 text-xs text-gray-400">
          This production server IP is locked and cannot be changed.
        </p>
      </div>
    </div>
  );
}